import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";


const SeamlessJourney = () => {
  const containerRef = useRef(null);
  const rowRef = useRef(null); 
  const autoIndexRef = useRef(0);
  const [activeStep, setActiveStep] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "center center"], 
  });

  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  useEffect(() => {
    const node = rowRef.current;
    let isPaused = false;

    if (!node) {
      return undefined;
    }

    const autoSlide = () => {
      const cards = Array.from(node.querySelectorAll(".journey-card-wrapper"));

      if (isPaused || window.innerWidth >= 992 || cards.length === 0) {
        return;
      }

      autoIndexRef.current = (autoIndexRef.current + 1) % cards.length;
      node.scrollTo({
        left: cards[autoIndexRef.current].offsetLeft,
        behavior: "smooth",
      });
    };

    const pauseSlider = () => {
      isPaused = true;
    };

    const resumeSlider = () => {
      isPaused = false;
      autoIndexRef.current = activeStep;
    };

    node.addEventListener("touchstart", pauseSlider, { passive: true });
    node.addEventListener("touchend", resumeSlider);
    node.addEventListener("touchcancel", resumeSlider);
    node.addEventListener("pointerdown", pauseSlider);
    node.addEventListener("pointerup", resumeSlider);
    node.addEventListener("pointercancel", resumeSlider);

    const intervalId = window.setInterval(autoSlide, 2000);

    return () => {
      window.clearInterval(intervalId);
      node.removeEventListener("touchstart", pauseSlider);
      node.removeEventListener("touchend", resumeSlider);
      node.removeEventListener("touchcancel", resumeSlider);
      node.removeEventListener("pointerdown", pauseSlider);
      node.removeEventListener("pointerup", resumeSlider);
      node.removeEventListener("pointercancel", resumeSlider);
    };
  }, [activeStep]);

  const handleScroll = () => {
    if (rowRef.current) {
      const cards = Array.from(rowRef.current.querySelectorAll(".journey-card-wrapper"));
      const scrollLeft = rowRef.current.scrollLeft;
      let index = 0;

      cards.forEach((card, cardIndex) => {
        if (Math.abs(card.offsetLeft - scrollLeft) < Math.abs((cards[index]?.offsetLeft ?? 0) - scrollLeft)) {
          index = cardIndex;
        }
      });

      setActiveStep(index);
    }
  };

  const steps = [
    {
      id: 1,
      title: "Your Personalized Circle Begins",
      desc: <>The moment you join, the "Old System" of being a random patient ends. Our AI and clinical team instantly analyze your taken health profile to handpick your <strong>Circle of 9 Medical Experts</strong>. We don’t just give you an app; we build a custom medical command center tailored specifically to your conditions and health needs.</>,
      img: "/Seamless1.png", 
      trigger: 0.1, 
    },
    {
      id: 2,
      title: "360° Management, Every Single Day",
      desc: <>This is where we leave traditional care behind. While hospitals wait for your next visit, we are with you daily. From <strong>consultations, at-home lab tests</strong>, and <strong>AI-powered daily
      symptom/vitals/mood tracking</strong> to <strong>Nutrition, Emotional Health, daily Garbhasanskar rituals</strong> and <strong>24/7 doctor access</strong>,  your journey is continuously monitored. We manage the details so you
      can enjoy the magic.</>,
      img: "/Seamless2.png",
      trigger: 0.5, 
    },
    {
      id: 3,
      title: "A Safer, Celebratory Arrival",
      desc: <>The result of constant care? <strong>Certainty</strong>. By detecting risks early and managing your
      health proactively, we reduce unnecessary C-sections and eliminate the stress of the unknown.
      You move from fragmented, confusing visits to a single, convenient, organized journey—arriving
      at motherhood feeling confident, healthy, and completely celebrated.</>,
      img: "/Seamless03.png",
      trigger: 0.9, 
    },
  ];

  const oldWayTags = ["Confusing", "Rushed", "Reactive", "Exhausting", "Lonely"];
  const newWayTags = ["Unhurried", "Guided", "Proactive", "Effortless", "Empowered"];

  return (
    <div className="main-combined-wrapper">
      <div className="journey-section" ref={containerRef}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="journey-title">
              From Day One to Delivery: A <span className="pink">Seamless Journey</span>
            </h2>
            <p className="journey-subtitle">
              Clarity is the greatest gift we can give you. Here is exactly how we walk beside you every step of the way.
            </p>
          </div>

          <div className="timeline-container">
            <div className="timeline-base-line"></div>
            {/* Desktop Animated Line */}
            <motion.div className="timeline-progress-line d-none d-md-block" style={{ scaleX }} />
            
            {steps.map((step, index) => (
              <TimelineDot 
                key={step.id} 
                step={step} 
                progress={scrollYProgress} 
                isActiveMobile={index <= activeStep} 
              />
            ))}
          </div>
          <div 
            className="row journey-cards-row" 
            ref={rowRef} 
            onScroll={handleScroll}
          >
            {steps.map((step) => (
              <div key={step.id} className="col-lg-4 col-md-6 mb-4 journey-card-wrapper">
                <div className="journey-card">
                  <div className="card-img-wrapper">
                    <img src={step.img} alt={step.title} />
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                  <span className="journey-step-tag">{`Step ${step.id}`}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="comparison-section container py-5">
        <div className="text-center mb-5">
          <h2 className="journey-title">
            The Care You Get vs. The <span className="pink">Care</span> You <span className="pink">Deserve</span>
          </h2>
          <p className="journey-subtitle">
            The traditional medical system treats pregnancy as a stressful checklist of rushed appointments. 
            We treat it as a 270-day daily supported and celebrated journey. Why manage a fragmented system 
            when you can have a complete sanctuary?
          </p>
        </div>

        <div className="row g-4 justify-content-center">
          <div className="col-md-5">
            <div className="comparison-card">
              <h3 className="card-label gray">The Fragmented System (The Old Way)</h3>
              <div className="card-img-container">
                <img src="/vs1.png" alt="Traditional Care" className="img-fluid rounded-4" />
              </div>
              <div className="tag-container mt-3">
                {oldWayTags.map((tag, index) => (
                  <React.Fragment key={tag}>
                    <span className="tag tag-old">
                      <i className="bi bi-heartbreak-fill me-1"></i> {tag}
                    </span>
                    {index < oldWayTags.length - 1 && <span style={{ margin: "0 4px" }}>|</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          <div className="col-md-5">
            <div className="comparison-card">
              <h3 className="card-label gray">The Myprega Ecosystem (Your New Reality)</h3>
              <div className="card-img-container">
                <img src="/vs2.png" alt="Myprega Care" className="img-fluid rounded-4" />
              </div>
              <div className="tag-container mt-3">
                {newWayTags.map((tag, index) => (
                  <React.Fragment key={tag}>
                    <span className="tag tag-new">
                      <i className="bi bi-heart-fill me-1"></i> {tag}
                    </span>
                    {index < newWayTags.length - 1 && <span style={{ margin: "0 4px" }}>|</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};


const TimelineDot = ({ step, progress, isActiveMobile }) => {
  const dotColor = useTransform(
    progress,
    [step.trigger - 0.05, step.trigger],
    ["#ffe0e6", "#FFA378"]
  );
  const textColor = useTransform(
    progress,
    [step.trigger - 0.05, step.trigger],
    ["#FFA378", "#ffffff"]
  );

  return (
    <motion.div 
      className={`timeline-dot dot-${step.id} ${isActiveMobile ? 'active' : ''}`}
      style={{ backgroundColor: dotColor }}
    >
      <motion.span style={{ color: textColor }}>{step.id}</motion.span>
    </motion.div>
  );
};

export default SeamlessJourney;
